'use client';
import React, { useState, useEffect } from 'react';
import JsonView from '@uiw/react-json-view';

/**
 * JSON Previewer Component using @uiw/react-json-view
 * @param {string} jsonInput - JSON string to preview
 * @param {string} [theme='light'] - Theme for the viewer (light/dark)
 * @param {number} [collapsed=1] - Initial collapse depth
 * @param {boolean} [enableClipboard=true] - Enable copy to clipboard
 * @param {boolean} [displayDataTypes=true] - Show data types
 * @param {boolean} [quotesOnKeys=true] - Show quotes on keys
 * @param {number} [indentWidth=2] - Indentation width
 * @returns {JSX.Element} Rendered JSON viewer or error message
 */
export function JsonPreviewer({
  jsonInput,
  theme = 'light',
  collapsed = 1,
  enableClipboard = true,
  displayDataTypes = true,
  quotesOnKeys = true,
  indentWidth = 2,
}) {
  const [parsedJson, setParsedJson] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    try {
      if (!jsonInput.trim()) {
        setParsedJson(null);
        setError(null);
        return;
      }

      const parsed = JSON.parse(jsonInput);
      setParsedJson(parsed);
      setError(null);
    } catch (err) {
      setError(`Invalid JSON: ${err.message}`);
      setParsedJson(null);
    }
  }, [jsonInput]);

  return (
    <div className="json-previewer-container">
      {error && (
        <div className="json-error-message" style={{ color: 'red', marginBottom: '1rem' }}>
          {error}
        </div>
      )}
      
      {parsedJson ? (
        <JsonView
          value={parsedJson}
          style={{
            backgroundColor: theme === 'dark' ? '#242424' : '#ffffff',
            fontFamily: 'monospace',
            fontSize: '14px',
            borderRadius: '4px',
            padding: '1rem',
          }}
          displayDataTypes={displayDataTypes}
          displayObjectSize={false}
          enableClipboard={enableClipboard}
          collapsed={collapsed}
          shortenTextAfterLength={100}
          indentWidth={indentWidth}
          theme={theme}
          quotesOnKeys={quotesOnKeys}
        />
      ) : !error && (
        <div className="json-empty-state" style={{ color: '#666', fontStyle: 'italic' }}>
          No JSON data to display
        </div>
      )}
    </div>
  );
}

export default JsonPreviewer;